import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Flame, CheckCircle2, Truck, XCircle, CalendarClock,
  ArrowLeft, MapPin, Utensils, ShoppingBag, Clock,
} from 'lucide-react';

const formatCLP = (n) =>
  new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

// Mismos estados que usa Cocina; el cliente ve solo los tres que le importan.
const PASOS = [
  { id: 'en_preparacion', label: 'En preparación', icon: Flame,        desc: 'La cocina ya está trabajando en tu pedido.' },
  { id: 'listo',          label: 'Listo',          icon: CheckCircle2, desc: 'Tu pedido está listo.' },
  { id: 'entregado',      label: 'Entregado',      icon: Truck,        desc: '¡Que lo disfrutes!' },
];

const TIPO_META = {
  mesa:     { icon: Utensils,    label: 'Para servir en mesa' },
  llevar:   { icon: ShoppingBag, label: 'Para llevar' },
  delivery: { icon: MapPin,      label: 'Delivery' },
};

const fmtHora = (ts) => {
  const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
  return d ? d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
};

export default function SeguimientoPedido() {
  const { id } = useParams();
  const [pedido, setPedido]   = useState(null);
  const [estado, setEstado]   = useState('cargando'); // cargando | ok | noexiste | error

  useEffect(() => {
    if (!id) return;
    const API = import.meta.env.VITE_BACKEND_URL ||
      'https://southamerica-east1-cocina-19123.cloudfunctions.net/api';

    // Consulta cada 15 s en vez de escuchar Firestore directo. Se pierde la
    // actualización instantánea, pero el documento de la comanda deja de ser
    // legible por cualquiera que tenga el enlace: el endpoint solo devuelve
    // estado, ítems y totales, nunca nombre, teléfono ni ubicación GPS.
    let vivo = true;
    const consultar = async () => {
      try {
        const r = await fetch(`${API}/api/public/pedido/${encodeURIComponent(id)}`);
        if (!vivo) return;
        if (r.status === 404) return setEstado('noexiste');
        const d = await r.json();
        if (!vivo) return;
        if (!r.ok || !d.ok) return setEstado('error');
        setPedido(d.pedido);
        setEstado('ok');
      } catch {
        if (vivo) setEstado('error');
      }
    };

    consultar();
    const intervalo = setInterval(consultar, 15000);
    return () => { vivo = false; clearInterval(intervalo); };
  }, [id]);

  if (estado === 'cargando') {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-brand-100 border-t-brand-500" />
      </div>
    );
  }

  if (estado !== 'ok') {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center space-y-4">
          <p className="text-5xl">🔍</p>
          <h1 className="text-xl font-black text-gray-900">No encontramos este pedido</h1>
          <p className="text-sm text-gray-500">
            El enlace puede estar incompleto. Si tienes dudas, comunícate con el restaurante.
          </p>
          <Link to="/menu" className="block w-full py-3.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-2xl transition-colors">
            Volver al menú
          </Link>
        </div>
      </div>
    );
  }

  const cancelado  = pedido.estado === 'cancelada';
  const programado = pedido.estado === 'programada';
  const pasoActual = PASOS.findIndex(p => p.id === pedido.estado);
  const Tipo       = TIPO_META[pedido.tipoPedido] || TIPO_META.llevar;

  return (
    <div className="min-h-screen bg-[#FDFCF8] pb-10">
      <div className="bg-brand-900 text-white px-4 pt-5 pb-8">
        <div className="max-w-md mx-auto">
          <Link to="/menu" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
            <ArrowLeft size={16} /> Volver al menú
          </Link>
          <p className="text-xs uppercase tracking-wider text-white/60 font-semibold mt-5">Pedido</p>
          <p className="text-4xl font-black">#{pedido.numero}</p>
          <p className="text-sm text-white/80 mt-1 flex items-center gap-1.5">
            <Tipo.icon size={15} /> {Tipo.label}
            {pedido.mesa && ` · Mesa ${pedido.mesa}`}
          </p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 -mt-4 space-y-4">
        {/* Estado */}
        <div className="bg-white rounded-3xl shadow-lg p-5 space-y-4">
          {cancelado ? (
            <div className="flex items-center gap-3 text-red-600">
              <XCircle size={28} className="shrink-0" />
              <div>
                <p className="font-black">Pedido cancelado</p>
                <p className="text-sm text-gray-500">Contáctanos si necesitas ayuda.</p>
              </div>
            </div>
          ) : programado ? (
            <div className="flex items-center gap-3 text-indigo-600">
              <CalendarClock size={28} className="shrink-0" />
              <div>
                <p className="font-black">Pedido programado</p>
                <p className="text-sm text-gray-500">
                  {pedido.fechaProgramada ? `Para el ${fmtHora(pedido.fechaProgramada)}` : 'Aún no entra a cocina.'}
                </p>
              </div>
            </div>
          ) : (
            PASOS.map((p, i) => {
              const hecho  = i <= pasoActual;
              const activo = i === pasoActual;
              return (
                <div key={p.id} className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 transition-colors
                    ${hecho ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-300'}`}>
                    <p.icon size={19} />
                  </div>
                  <div className="pt-1">
                    <p className={`font-bold text-sm ${hecho ? 'text-gray-900' : 'text-gray-400'}`}>{p.label}</p>
                    {activo && <p className="text-xs text-gray-500 mt-0.5">{p.desc}</p>}
                  </div>
                </div>
              );
            })
          )}

          {pedido.fechaActualizacion && !cancelado && (
            <p className="text-[11px] text-gray-400 flex items-center gap-1.5 pt-1 border-t border-gray-100">
              <Clock size={12} /> Actualizado {fmtHora(pedido.fechaActualizacion)}
            </p>
          )}
        </div>

        {/* Detalle */}
        <div className="bg-white rounded-3xl shadow-lg p-5 space-y-3">
          <p className="font-black text-gray-900">Detalle</p>
          {(pedido.items || []).map((it, i) => (
            <div key={i} className="flex items-start gap-3 text-sm">
              <span className="font-black text-brand-600 shrink-0">{it.cantidad}×</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800">{it.nombre}</p>
                {it.nota && <p className="text-xs text-gray-400">{it.nota}</p>}
              </div>
              <span className="font-bold text-gray-700 shrink-0">{formatCLP(it.subtotal)}</span>
            </div>
          ))}

          <div className="pt-3 border-t border-gray-100 space-y-1 text-sm">
            {pedido.descuento > 0 && (
              <div className="flex justify-between text-green-600 font-semibold">
                <span>Descuento {pedido.codigoPromo ? `(${pedido.codigoPromo})` : ''}</span>
                <span>−{formatCLP(pedido.descuento)}</span>
              </div>
            )}
            <div className="flex justify-between font-black text-base text-gray-900">
              <span>Total</span>
              <span>{formatCLP(pedido.total)}</span>
            </div>
            {pedido.metodoPago && (
              <p className="text-xs text-gray-500 capitalize pt-1">Pago: {pedido.metodoPago}</p>
            )}
          </div>

          {pedido.direccion && (
            <p className="text-xs text-gray-500 flex items-start gap-1.5 pt-2 border-t border-gray-100">
              <MapPin size={13} className="shrink-0 mt-0.5" /> {pedido.direccion}
            </p>
          )}
          {pedido.notas && <p className="text-xs text-gray-500 italic">“{pedido.notas}”</p>}
        </div>

        <Link to="/menu" className="block text-center py-3.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-2xl transition-colors">
          Hacer otro pedido
        </Link>
      </div>
    </div>
  );
}
